import { BCMSCurrency, DisabledPrompt, useBCMSModal, useStateManager } from "@bcm-dev/bcms-vite";
import CustomSelect, { CustomSelectItem } from "components/custom-ui/CustomSelect";
import { TableBody, TableView } from 'components/shared';
import { PrimaryButton, SecondaryButton } from 'components/ui';
import { SVGAsset } from "res";

/**
 * @function TASPositivePayExceptionChecksDesktopContentView
 * @return {React.Component}
 * @exported true
 * @description The "TAS Positive Pay Exception Checks" desktop content view.
 */
export default function TASPositivePayExceptionChecksDesktopContentView(props) { 
  const { data, ui, onSaveClick } = useStateManager();
  const exceptionCheckForms = data.exceptionCheckForms;
  const exceptionCheckFormsUI = ui.exceptionCheckForms;
  const userEditedCheckForms = data.search.userEditedCheckForms().length > 0;
  const buttonClasses = (userEditedCheckForms || ui.search.saveAndCancelButtons.isActive)
  ? { className: "set-width" }
  : { className: "set-width disabled" };

  const search = data.search;
  const editExceptionChecksHaveReturnReasonError = search.editExceptionChecksHaveReturnReasonError;

  const rows = exceptionCheckForms.reduce((acc, item, index) => {
    const amount = item.amount ? item.amount.amount : undefined;
    const isEditing = exceptionCheckFormsUI[index].isEditing;
    const editItem = data.search.editExceptionCheckForms.filter(_item => _item.id === item.id)[0];
    
    acc.push(
      isEditing
      ? <EditingRow key={ item.id } amount={ amount  } item={ item } editItem={ editItem }/>
      : <Row key={ item.id } amount={ amount  } item={ item }/>
    );
    return acc;
  }, []);

  return (
      <div className="flex-container flex-vertical table-view-container show-on-desktop">
        <TableView className="tas-positive-pay-add-check-table">
            <thead className="table-column-container">
            <tr>
                <th className="text-left table-column-view">Account</th>
                <th className="text-left table-column-view">Issued Date</th>
                <th className="text-left table-column-view">Check Number</th>
                <th className="text-left table-column-view">Amount</th>
                <th className="text-left table-column-view">Payee</th>
                <th className="text-left table-column-view">Reason</th>
                <th className="text-left table-column-view">Action</th>
                <th className="text-left table-column-view">Return Reason</th>
            </tr>
            </thead>
            <TableBody>{ rows } </TableBody>
        </TableView>
        { editExceptionChecksHaveReturnReasonError
        && <div className="error-alert-info-container">
              <SVGAsset id="AlertTriangleIcon" className="" />
              <span>{ `Please provide return reason(s).` }</span>
          </div>
        }
        <div className="flex-container flex-horizontal button-container">
          <DisabledPrompt>
            <SecondaryButton { ...buttonClasses } text="Cancel" dataPoint="TAS_Positive_Pay_Exception_Checks_Cancel_Button" onClick={ undefined } />
          </DisabledPrompt>
            <PrimaryButton { ...buttonClasses } text="Save" dataPoint="TAS_Positive_Pay_Exception_Checks_Save_Button" onClick={ onSaveClick } />
        </div>
      </div>
  );
}

/**
 * @function EditingRow
 * @return {React.Component}
 * @exported false
 * @description A row that can be edited by the user.
 */
function EditingRow(props) {
  const { data, ui, onEdit, onReturnReasonClick } = useStateManager();
  const item = props.editItem ?? {};
  const uiItem = ui.exceptionCheckForms.filter(_item => _item.exceptionCheckForm.id === item.id)[0];
  const amount = props.amount ?? "";
  const search = data.search;
  const showReturnReasonSelect = uiItem.showReturnReasonSelect;
  const returnReasonHasError = uiItem.returnReasonHasError;
  const returnReasonSelectClasses = returnReasonHasError
  ? { className: `return-reason-select select-error` }
  : { className: `return-reason-select` };

  return (
    <tr key={ item.id } className="table-item-container">
      <td className="table-item-view">
        <span data-point={ `TAS_Positive_Pay_Exception_Checks_Desktop_Edit_Row_Account_${ item.id }`}>{ `${ item.account.name } ${ item.account.number }`}</span>
      </td>
      <td className="table-item-view">
        <span data-point={ `TAS_Positive_Pay_Exception_Checks_Desktop_Edit_Row_Issued_Date_${ item.id }`}>{ `${ item.issuedDate.format("m/d/Y") }` }</span>
      </td>
      <td className="table-item-view">
        <DisabledPrompt>
          <a className="clickable">
            <span data-point={ `TAS_Positive_Pay_Exception_Checks_Desktop_Edit_Row_Check_Number_${ item.id }`}>{ `${ item.checkNumber }` }</span>
          </a>
        </DisabledPrompt>
      </td>
      <td className="table-item-view">
        <span data-point={ `TAS_Positive_Pay_Exception_Checks_Desktop_Edit_Row_Amount_${ item.id }`}>{ `${ BCMSCurrency.init(amount).format() }` }</span>
      </td>
      <td className="table-item-view">
        <span data-point={ `TAS_Positive_Pay_Exception_Checks_Desktop_Edit_Row_Payee_${ item.id }`}>{ `${ item.payee }` }</span>
      </td>
      <td className="table-item-view reason">
        <span data-point={ `TAS_Positive_Pay_Exception_Checks_Desktop_Edit_Row_Reason_${ item.id }`}>{ `${ item.reason }` }</span>
      </td>
      <td className="table-item-view action">
        <CustomSelect active={  uiItem.action.isActive } value={ item.action }
        dataPoint={ `TAS_Positive_Pay_Exception_Checks_Desktop_Edit_Row_Action_${ item.id }`}
        onChange={ onEdit(item.id, "action") }>
            <CustomSelectItem value="prompt" className="prompt"
            dataPoint={ `TAS_Positive_Pay_Exception_Checks_Desktop_Edit_Row_Action_Option_Prompt_${ item.id }` }></CustomSelectItem>
            <CustomSelectItem value="approve"
            dataPoint={ `TAS_Positive_Pay_Exception_Checks_Desktop_Edit_Row_Action_Option_Approve_${ item.id }` }>Approve</CustomSelectItem>
            <CustomSelectItem value="return"
            dataPoint={ `TAS_Positive_Pay_Exception_Checks_Desktop_Edit_Row_Action_Option_Return_${ item.id }` }>Return</CustomSelectItem>
        </CustomSelect>
      </td>
      <td className="table-item-view return-reason">
        { showReturnReasonSelect && 
        <span onClick={ onReturnReasonClick(item.id) }>
          <CustomSelect { ...returnReasonSelectClasses } active={  uiItem.returnReason.isActive } value={ item.returnReason }
          dataPoint={ `TAS_Positive_Pay_Exception_Checks_Desktop_Edit_Row_Return_Reason_${ item.id }`}
          onChange={ onEdit(item.id, "returnReason") }>
              <CustomSelectItem value="forgery"
              dataPoint={ `TAS_Positive_Pay_Exception_Checks_Desktop_Edit_Row_Return_Reason_Option_Forgery_${ item.id }` }>Forgery</CustomSelectItem>
              <CustomSelectItem value="altered/fictitious"
              dataPoint={ `TAS_Positive_Pay_Exception_Checks_Desktop_Edit_Row_Return_Reason_Option_Altered_${ item.id }` }>Altered/Fictitious</CustomSelectItem>
              <CustomSelectItem value="duplicate presentmen"
              dataPoint={ `TAS_Positive_Pay_Exception_Checks_Desktop_Edit_Row_Return_Reason_Option_Duplicate_${ item.id }` }>Duplicate Presentmen</CustomSelectItem>
              <CustomSelectItem value="refer to maker"
              dataPoint={ `TAS_Positive_Pay_Exception_Checks_Desktop_Edit_Row_Return_Reason_Option_Refer_${ item.id }` }>Refer to Maker</CustomSelectItem>
          </CustomSelect>
        </span>
        }
      </td>
    </tr>
  );
}

/**
 * @function Row
 * @return {React.Component}
 * @exported false
 * @description NOT CURRENTLY IN USE. This is a row that can only be read by the user.
 */
function Row(props) {
  const { onChange, onEditClick } = useStateManager();
  const item = props.item ?? {};
  const amount = props.amount ?? "";

  return (
    <tr key={ item.id } className="table-item-container">
      <td className="table-item-view">
        <span data-point={ `TAS_Positive_Pay_Exception_Checks_Desktop_Row_Account_${ item.id }`}>{ `${ item.account.name } ${ item.account.number }`}</span>
      </td>
      <td className="table-item-view">
        <span data-point={ `TAS_Positive_Pay_Exception_Checks_Desktop_Row_Issued_Date_${ item.id }`}>{ `${ item.issuedDate.format("m/d/Y") }` }</span>
      </td>
      <td className="table-item-view">
        <span data-point={ `TAS_Positive_Pay_Exception_Checks_Desktop_Row_Check_Number_${ item.id }`}>{ `${ item.checkNumber }` }</span>
      </td>
      <td className="table-item-view">
        <span data-point={ `TAS_Positive_Pay_Exception_Checks_Desktop_Row_Amount_${ item.id }`}>{ `${ BCMSCurrency.init(amount).format() }` }</span>
      </td>
      <td className="table-item-view">
        <span data-point={ `TAS_Positive_Pay_Exception_Checks_Desktop_Row_Payee_${ item.id }`}>{ `${ item.payee }` }</span>
      </td>
      <td className="table-item-view">
        <span data-point={ `TAS_Positive_Pay_Exception_Checks_Desktop_Row_Reference_Number_${ item.id }`}>{ `${ item.referenceNumber }` }</span>
      </td>
      <td className="table-item-view capitalized-item-view">
        <span data-point={ `TAS_Positive_Pay_Exception_Checks_Desktop_Row_Type_${ item.id }`}>{ `${ item.type }` }</span>
      </td>
      <td className="table-item-view capitalized-item-view">
        <span data-point={ `TAS_Positive_Pay_Exception_Checks_Desktop_Row_Status_${ item.id }`}>{ `${ item.status }` }</span>
      </td>  
      <td className="table-item-view">
        <SVGAsset id="EditIcon" className="clickable" data-point={ `TAS_Positive_Pay_Exception_Checks_Desktop_Row_Edit_Button_${ item.id }`}
        onClick={ onEditClick(item.id) } />
      </td>
      <td className="table-item-view">
        <DisabledPrompt>
            <SVGAsset id="TrashIcon" className="clickable" data-point={ `TAS_Positive_Pay_Exception_Checks_Desktop_Row_Delete_Button_${ item.id }`} />
        </DisabledPrompt>
      </td>
    </tr>
  );
}